import type { Category } from '@arm-pd/domain';
import { Loading } from './Loading';
import { ErrorMessage, EmptyState } from './ErrorMessage';

interface CategoryBrowserProps {
  categories: Category[] | undefined;
  selectedCategoryId: string | undefined;
  onSelect: (categoryId: string | undefined) => void;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
}

/**
 * Category browser component for selecting a category
 * Displays categories as a list with hierarchy support
 */
export function CategoryBrowser({
  categories,
  selectedCategoryId,
  onSelect,
  isLoading,
  error,
  onRetry,
}: CategoryBrowserProps) {
  const onRetryFn = onRetry ?? (() => {});
  if (isLoading) {
    return (
      <div className="py-6">
        <h3 className="mb-4 text-base font-medium text-gray-900">Categories</h3>
        <Loading message="Loading categories..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6">
        <h3 className="mb-4 text-base font-medium text-gray-900">Categories</h3>
        <ErrorMessage
          title="Failed to load categories"
          message={error.message}
          onRetry={onRetryFn}
        />
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="py-6">
        <h3 className="mb-4 text-base font-medium text-gray-900">Categories</h3>
        <EmptyState title="No categories" message="This catalog has no categories." />
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-medium text-gray-900">Categories</h3>
        {selectedCategoryId && (
          <button
            type="button"
            onClick={() => onSelect(undefined)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Show all
          </button>
        )}
      </div>

      <nav className="mt-4" aria-label="Product categories">
        <ul className="space-y-2">
          {/* All categories option */}
          <li>
            <button
              type="button"
              onClick={() => onSelect(undefined)}
              className={`
                w-full rounded-md px-4 py-3 text-left text-sm font-medium transition-colors
                ${
                  !selectedCategoryId
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                touch-min-height
              `}
              aria-pressed={!selectedCategoryId}
            >
              All Products
            </button>
          </li>

          {/* Category list */}
          {categories.map(category => (
            <li key={category.id}>
              <button
                type="button"
                onClick={() => onSelect(category.id)}
                className={`
                  w-full rounded-md px-4 py-3 text-left text-sm font-medium transition-colors
                  ${
                    selectedCategoryId === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  touch-min-height
                `}
                aria-pressed={selectedCategoryId === category.id}
              >
                <div className="flex items-center justify-between">
                  <span>{category.name}</span>
                  {category.hasSubCategories && (
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                </div>
              </button>

              {/* Sub-categories */}
              {category.hasSubCategories &&
                category.subCategories &&
                selectedCategoryId === category.id && (
                  <ul className="mt-2 space-y-1 pl-4">
                    {category.subCategories.map(subCategory => (
                      <li key={subCategory.id}>
                        <button
                          type="button"
                          disabled
                          className="w-full rounded-md bg-gray-50 px-4 py-2 text-left text-sm text-gray-500"
                        >
                          {subCategory.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
